/**
 * QualityScoringPass — Evaluates generated maps against quality rules.
 *
 * Runs after all other passes and produces a report card with:
 *   - Pass/fail for each rule
 *   - Specific violation locations (grid coordinates)
 *   - An overall quality score (0-100)
 *   - Suggested fixes for each violation type
 *
 * The goal: every time a generator produces a bad map, we can identify
 * exactly what's wrong, fix the generator, and ensure it never produces
 * that problem again. Generators get smarter over time.
 *
 * Rules are weighted by severity:
 *   CRITICAL (0 tolerance) — game-breaking: unreachable rooms, blocked spawns
 *   MAJOR (heavy penalty)  — ugly but playable: orphan walls, wrong tile orientation
 *   MINOR (small penalty)  — polish: missing variants, sparse decoration
 */

import { ZONE } from '../ZoneTypes.js';

// Rule severity weights
const CRITICAL = 0;   // Any violation = fail
const MAJOR = 10;     // Each violation costs 10 points
const MINOR = 2;      // Each violation costs 2 points

export class QualityScoringPass {
  constructor(tileMeta = null) {
    this.meta = tileMeta;
  }

  /**
   * Score a generated map.
   *
   * @param {Object} mapData — Generator output, expected shape:
   *   { ground: number[][], objects: number[][], rooms: Array, width, height }
   *   ground = 2D grid of tile/zone IDs for the base layer
   *   objects = 2D grid for walls/doors/furniture layer
   *   rooms = array of { x, y, w, h, type, isSpawn } room definitions
   * @param {Object} [options] — { skipRules: string[] }
   * @returns {QualityReport}
   */
  score(mapData, options = {}) {
    const { ground, objects, rooms, width, height } = mapData;
    const skipRules = new Set(options.skipRules || []);

    const violations = [];
    let maxScore = 100;

    const rules = [
      // CRITICAL — game-breaking
      { id: 'room-connectivity',   severity: CRITICAL, fn: () => this._checkRoomConnectivity(ground, objects, rooms, width, height) },
      { id: 'spawn-accessible',    severity: CRITICAL, fn: () => this._checkSpawnAccessible(ground, objects, rooms, width, height) },
      { id: 'exterior-exits',      severity: CRITICAL, fn: () => this._checkExteriorExits(ground, objects, rooms, width, height) },

      // MAJOR — ugly but playable
      { id: 'orphan-walls',        severity: MAJOR,    fn: () => this._checkOrphanWalls(objects, width, height) },
      { id: 'doors-both-sides',    severity: MAJOR,    fn: () => this._checkDoorsBothSides(ground, objects, width, height) },
      { id: 'windows-on-exterior', severity: MAJOR,    fn: () => this._checkWindowsExterior(ground, objects, width, height) },
      { id: 'floor-zone-match',    severity: MAJOR,    fn: () => this._checkFloorZoneMatch(ground, rooms, width, height) },
      { id: 'no-outdoor-floors',   severity: MAJOR,    fn: () => this._checkNoOutdoorFloors(ground, objects, rooms, width, height) },
      { id: 'wall-continuity',     severity: MAJOR,    fn: () => this._checkWallContinuity(objects, width, height) },

      // MINOR — polish
      { id: 'decoration-density',  severity: MINOR,    fn: () => this._checkDecorationDensity(objects, rooms, width, height) },
      { id: 'tile-variety',        severity: MINOR,    fn: () => this._checkTileVariety(ground, width, height) },
      { id: 'dead-ends',           severity: MINOR,    fn: () => this._checkDeadEnds(ground, objects, width, height) },
    ];

    for (const rule of rules) {
      if (skipRules.has(rule.id)) continue;

      const result = rule.fn();
      if (result.violations.length > 0) {
        const entry = {
          rule: rule.id,
          severity: rule.severity === CRITICAL ? 'critical' : rule.severity === MAJOR ? 'major' : 'minor',
          count: result.violations.length,
          locations: result.violations.slice(0, 20), // cap at 20 for readability
          message: result.message,
          fix: result.fix || null,
        };
        violations.push(entry);

        if (rule.severity === CRITICAL) {
          maxScore = 0; // instant fail
        } else {
          maxScore -= rule.severity * result.violations.length;
        }
      }
    }

    const score = Math.max(0, Math.min(100, maxScore));
    const grade = score >= 90 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 40 ? 'D' : 'F';
    const passed = violations.filter(v => v.severity === 'critical').length === 0;

    return {
      score,
      grade,
      passed,
      violations,
      summary: {
        critical: violations.filter(v => v.severity === 'critical').length,
        major: violations.filter(v => v.severity === 'major').length,
        minor: violations.filter(v => v.severity === 'minor').length,
        totalViolations: violations.reduce((sum, v) => sum + v.count, 0),
      },
    };
  }

  // ─── CRITICAL Rules ──────────────────────────────────────────

  /**
   * All rooms must be reachable from the spawn room via walkable tiles.
   */
  _checkRoomConnectivity(ground, objects, rooms, w, h) {
    if (!rooms || rooms.length < 2) return { violations: [], message: 'OK' };

    const walkable = this._buildWalkabilityGrid(ground, objects, w, h);
    const spawnRoom = rooms.find(r => r.isSpawn) || rooms[0];

    // Find a walkable tile in the spawn room (center might be a wall)
    let startX = -1, startY = -1;
    for (let ry = spawnRoom.y; ry < spawnRoom.y + spawnRoom.h && startX === -1; ry++) {
      for (let rx = spawnRoom.x; rx < spawnRoom.x + spawnRoom.w && startX === -1; rx++) {
        if (rx >= 0 && ry >= 0 && rx < w && ry < h && walkable[ry][rx]) {
          startX = rx;
          startY = ry;
        }
      }
    }

    // If no walkable tile in spawn room, connectivity check can't run
    if (startX === -1) {
      return {
        violations: [{ x: spawnRoom.x, y: spawnRoom.y, room: 'spawn' }],
        message: 'Spawn room has no walkable tiles — cannot check connectivity',
        fix: 'Ensure spawn room interior is not fully blocked by walls or objects.',
      };
    }

    // BFS from first walkable tile in spawn room
    const visited = new Set();
    const queue = [[startX, startY]];
    visited.add(`${startX},${startY}`);

    while (queue.length > 0) {
      const [cx, cy] = queue.shift();
      for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
        const nx = cx + dx, ny = cy + dy;
        const key = `${nx},${ny}`;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        if (visited.has(key)) continue;
        if (!walkable[ny][nx]) continue;
        visited.add(key);
        queue.push([nx, ny]);
      }
    }

    // Check each room has at least one reachable tile
    const unreachable = [];
    for (const room of rooms) {
      let hasReachable = false;
      for (let ry = room.y; ry < room.y + room.h && !hasReachable; ry++) {
        for (let rx = room.x; rx < room.x + room.w && !hasReachable; rx++) {
          if (visited.has(`${rx},${ry}`)) hasReachable = true;
        }
      }
      if (!hasReachable) {
        unreachable.push({ x: room.x, y: room.y, room: room.type || 'unknown' });
      }
    }

    return {
      violations: unreachable,
      message: unreachable.length > 0
        ? `${unreachable.length} room(s) unreachable from spawn`
        : 'All rooms connected',
      fix: 'Ensure doors connect all rooms. Check for missing doorframes in interior walls.',
    };
  }

  /**
   * Spawn room must have walkable tiles and not be blocked.
   */
  _checkSpawnAccessible(ground, objects, rooms, w, h) {
    const spawnRoom = rooms?.find(r => r.isSpawn);
    if (!spawnRoom) return { violations: [{ x: 0, y: 0 }], message: 'No spawn room defined', fix: 'Mark one room as isSpawn.' };

    const walkable = this._buildWalkabilityGrid(ground, objects, w, h);
    let walkableCount = 0;
    for (let y = spawnRoom.y; y < spawnRoom.y + spawnRoom.h; y++) {
      for (let x = spawnRoom.x; x < spawnRoom.x + spawnRoom.w; x++) {
        if (x >= 0 && y >= 0 && x < w && y < h && walkable[y][x]) walkableCount++;
      }
    }

    const minWalkable = 4; // need at least 4 walkable tiles to spawn
    if (walkableCount < minWalkable) {
      return {
        violations: [{ x: spawnRoom.x, y: spawnRoom.y, walkable: walkableCount }],
        message: `Spawn room only has ${walkableCount} walkable tiles (need ${minWalkable}+)`,
        fix: 'Reduce decorations in spawn room. Ensure no walls block the interior.',
      };
    }

    return { violations: [], message: 'Spawn room accessible' };
  }

  /**
   * Ground floor must have at least one exit to the outside.
   */
  _checkExteriorExits(ground, objects, rooms, w, h) {
    // Look for door tiles adjacent to outdoor terrain
    const exits = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!this._isDoor(objects, x, y)) continue;
        // Check if any neighbor is outdoor terrain
        for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < w && ny < h && this._isOutdoor(ground, nx, ny)) {
            exits.push({ x, y });
            break;
          }
        }
      }
    }

    if (exits.length === 0) {
      return {
        violations: [{ x: 0, y: 0 }],
        message: 'No exterior exits found — building has no way in/out',
        fix: 'Place at least 2 doors on exterior walls adjacent to outdoor terrain.',
      };
    }

    return { violations: [], message: `${exits.length} exterior exit(s) found` };
  }

  // ─── MAJOR Rules ──────────────────────────────────────────────

  /**
   * No isolated 1-tile wall segments (walls with no wall neighbors).
   */
  _checkOrphanWalls(objects, w, h) {
    const orphans = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!this._isWall(objects, x, y)) continue;
        let wallNeighbors = 0;
        for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
          if (this._isWall(objects, x + dx, y + dy)) wallNeighbors++;
        }
        if (wallNeighbors === 0) {
          orphans.push({ x, y });
        }
      }
    }

    return {
      violations: orphans,
      message: orphans.length > 0 ? `${orphans.length} orphan wall tile(s)` : 'No orphan walls',
      fix: 'Remove isolated wall tiles or connect them to adjacent walls.',
    };
  }

  /**
   * Every door must have walkable tiles on both sides.
   */
  _checkDoorsBothSides(ground, objects, w, h) {
    const walkable = this._buildWalkabilityGrid(ground, objects, w, h);
    const badDoors = [];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!this._isDoor(objects, x, y)) continue;

        // Check horizontal (left/right should be walkable)
        const leftOk = x > 0 && walkable[y][x - 1];
        const rightOk = x < w - 1 && walkable[y][x + 1];
        const topOk = y > 0 && walkable[y - 1][x];
        const bottomOk = y < h - 1 && walkable[y + 1][x];

        // Door should have walkable on at least 2 opposite sides
        const hPair = leftOk && rightOk;
        const vPair = topOk && bottomOk;

        if (!hPair && !vPair) {
          badDoors.push({ x, y });
        }
      }
    }

    return {
      violations: badDoors,
      message: badDoors.length > 0 ? `${badDoors.length} door(s) don't connect two walkable areas` : 'All doors properly connected',
      fix: 'Ensure doors are placed in walls with floor tiles on both sides.',
    };
  }

  /**
   * Windows should only appear on exterior walls (walls bordering outdoor terrain).
   */
  _checkWindowsExterior(ground, objects, w, h) {
    const badWindows = [];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!this._isWindow(objects, x, y)) continue;

        let touchesOutdoor = false;
        for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < w && ny < h && this._isOutdoor(ground, nx, ny)) {
            touchesOutdoor = true;
            break;
          }
        }

        if (!touchesOutdoor) {
          badWindows.push({ x, y });
        }
      }
    }

    return {
      violations: badWindows,
      message: badWindows.length > 0 ? `${badWindows.length} window(s) on interior walls` : 'All windows on exterior',
      fix: 'Only place windows on walls that border outdoor terrain.',
    };
  }

  /**
   * Floor tiles should match their room's zone assignment.
   */
  _checkFloorZoneMatch(ground, rooms, w, h) {
    if (!this.meta || !rooms) return { violations: [], message: 'Skipped (no TileMeta)' };

    const mismatches = [];
    for (const room of rooms) {
      if (!room.type) continue;
      const validFloors = this.meta.getFloorsForZone(room.type);
      if (validFloors.length === 0) continue;

      const validTileIds = new Set();
      for (const ft of validFloors) {
        validTileIds.add(ft.tile);
        for (const v of (ft.variants || [])) validTileIds.add(v);
      }

      for (let y = room.y; y < room.y + room.h; y++) {
        for (let x = room.x; x < room.x + room.w; x++) {
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          const tile = ground[y]?.[x];
          if (tile != null && this.meta.getRole(tile) === 'floor' && !validTileIds.has(tile)) {
            mismatches.push({ x, y, room: room.type, tile });
          }
        }
      }
    }

    return {
      violations: mismatches,
      message: mismatches.length > 0 ? `${mismatches.length} tile(s) don't match room zone` : 'Floor tiles match zones',
      fix: 'Use TileMeta.getFloorsForZone() to pick correct floor tiles per room type.',
    };
  }

  /**
   * No indoor floor tiles should appear in outdoor areas.
   */
  _checkNoOutdoorFloors(ground, objects, rooms, w, h) {
    const outdoorFloors = [];
    const roomBounds = this._buildRoomMask(rooms, w, h);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (roomBounds[y]?.[x]) continue; // inside a room, skip
        const tile = ground[y]?.[x];
        if (tile != null && this._isFloorTile(tile) && !this._isOutdoorTile(tile)) {
          outdoorFloors.push({ x, y, tile });
        }
      }
    }

    return {
      violations: outdoorFloors,
      message: outdoorFloors.length > 0 ? `${outdoorFloors.length} indoor floor tile(s) in outdoor areas` : 'No misplaced floors',
      fix: 'Ensure outdoor areas only use terrain tiles (grass, asphalt, sidewalk).',
    };
  }

  /**
   * Wall segments should form continuous lines, not have gaps (except doors).
   */
  _checkWallContinuity(objects, w, h) {
    const gaps = [];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!this._isWall(objects, x, y)) continue;

        // Check for wall-gap-wall patterns (horizontal and vertical)
        // Horizontal: wall at x, empty at x+1, wall at x+2
        if (x + 2 < w) {
          if (!this._isWall(objects, x + 1, y) && !this._isDoor(objects, x + 1, y) &&
              this._isWall(objects, x + 2, y)) {
            gaps.push({ x: x + 1, y, direction: 'horizontal' });
          }
        }
        // Vertical: wall at y, empty at y+1, wall at y+2
        if (y + 2 < h) {
          if (!this._isWall(objects, x, y + 1) && !this._isDoor(objects, x, y + 1) &&
              this._isWall(objects, x, y + 2)) {
            gaps.push({ x, y: y + 1, direction: 'vertical' });
          }
        }
      }
    }

    return {
      violations: gaps,
      message: gaps.length > 0 ? `${gaps.length} gap(s) in wall lines` : 'Walls continuous',
      fix: 'Fill wall gaps or place doors in intentional openings.',
    };
  }

  // ─── MINOR Rules ──────────────────────────────────────────────

  /**
   * Rooms should have at least some decorations (not bare).
   */
  _checkDecorationDensity(objects, rooms, w, h) {
    if (!rooms) return { violations: [], message: 'Skipped (no rooms)' };

    const bare = [];
    for (const room of rooms) {
      if (room.isSpawn) continue; // spawn rooms can be sparse
      let decoCount = 0;
      for (let y = room.y; y < room.y + room.h; y++) {
        for (let x = room.x; x < room.x + room.w; x++) {
          if (x >= 0 && y >= 0 && x < w && y < h) {
            const tile = objects[y]?.[x];
            if (tile != null && tile !== 0 && !this._isWall(objects, x, y) && !this._isDoor(objects, x, y)) {
              decoCount++;
            }
          }
        }
      }
      const area = room.w * room.h;
      if (area > 8 && decoCount === 0) {
        bare.push({ x: room.x, y: room.y, room: room.type || 'unknown', area });
      }
    }

    return {
      violations: bare,
      message: bare.length > 0 ? `${bare.length} room(s) have no decorations` : 'Rooms decorated',
      fix: 'Add at least 1 decoration per non-spawn room with area > 8 tiles.',
    };
  }

  /**
   * Ground terrain should use tile variants for visual variety.
   */
  _checkTileVariety(ground, w, h) {
    // Count how many tiles use the exact same ID
    const counts = new Map();
    let total = 0;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const tile = ground[y]?.[x];
        if (tile == null) continue;
        counts.set(tile, (counts.get(tile) || 0) + 1);
        total++;
      }
    }

    // If any single tile accounts for >70% of the map, it's too uniform
    const violations = [];
    for (const [tile, count] of counts) {
      const ratio = count / total;
      if (ratio > 0.7 && total > 100) {
        violations.push({ tile, count, percent: Math.round(ratio * 100) });
      }
    }

    return {
      violations,
      message: violations.length > 0 ? `${violations.length} tile(s) dominate >70% of map` : 'Good tile variety',
      fix: 'Use random variants (randomVariants in TileMeta) to break visual monotony.',
    };
  }

  /**
   * Check for dead-end corridors (walkable tiles with only 1 walkable neighbor).
   */
  _checkDeadEnds(ground, objects, w, h) {
    const walkable = this._buildWalkabilityGrid(ground, objects, w, h);
    const deadEnds = [];

    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (!walkable[y][x]) continue;
        let neighbors = 0;
        for (const [dx, dy] of [[0,-1],[1,0],[0,1],[-1,0]]) {
          if (walkable[y + dy]?.[x + dx]) neighbors++;
        }
        if (neighbors === 1) {
          deadEnds.push({ x, y });
        }
      }
    }

    return {
      violations: deadEnds,
      message: deadEnds.length > 0 ? `${deadEnds.length} dead-end tile(s)` : 'No dead ends',
      fix: 'Widen corridors or add alternate routes. Dead ends frustrate navigation.',
    };
  }

  // ─── Helpers ──────────────────────────────────────────────────

  _buildWalkabilityGrid(ground, objects, w, h) {
    const grid = [];
    for (let y = 0; y < h; y++) {
      const row = [];
      for (let x = 0; x < w; x++) {
        const gTile = ground[y]?.[x] ?? -1;
        const oTile = objects[y]?.[x] ?? 0;
        // Walkable = has a ground/floor tile and no blocking object tile
        const hasGround = gTile >= 0;
        const blocked = this._isWall(objects, x, y);
        row.push(hasGround && !blocked);
      }
      grid.push(row);
    }
    return grid;
  }

  _buildRoomMask(rooms, w, h) {
    const mask = [];
    for (let y = 0; y < h; y++) mask.push(new Array(w).fill(false));
    if (!rooms) return mask;
    for (const room of rooms) {
      for (let y = room.y; y < room.y + room.h; y++) {
        for (let x = room.x; x < room.x + room.w; x++) {
          if (x >= 0 && y >= 0 && x < w && y < h) mask[y][x] = true;
        }
      }
    }
    return mask;
  }

  // Tile type checks — override these if your tileset uses different conventions
  // Default implementation uses ZoneTypes and common tile ID ranges

  _isWall(grid, x, y) {
    if (!grid || x < 0 || y < 0 || y >= grid.length || x >= grid[0]?.length) return false;
    const t = grid[y][x];
    if (this.meta) return this.meta.getRole(t) === 'wall';
    // Fallback: common wall zones
    return t === ZONE.WALL || t === ZONE.WALL_EXT || t === ZONE.WALL_INT ||
           t === ZONE.WALL_H || t === ZONE.WALL_V || t === ZONE.WALL_CORNER || t === ZONE.WALL_TOP;
  }

  _isDoor(grid, x, y) {
    if (!grid || x < 0 || y < 0 || y >= grid.length || x >= grid[0]?.length) return false;
    const t = grid[y][x];
    if (this.meta) return this.meta.getRole(t) === 'opening';
    return t === ZONE.DOOR || t === ZONE.DOORFRAME;
  }

  _isWindow(grid, x, y) {
    if (!grid || x < 0 || y < 0 || y >= grid.length || x >= grid[0]?.length) return false;
    const t = grid[y][x];
    if (this.meta) {
      if (this.meta.getRole(t) !== 'opening') return false;
      // Check all openings of type 'window' via public API
      for (const opening of this.meta.getOpeningsByType('window')) {
        if (opening.tile === t || opening.tileBroken === t) return true;
      }
      return false;
    }
    return t === ZONE.WINDOW;
  }

  _isOutdoor(ground, x, y) {
    if (!ground || x < 0 || y < 0 || y >= ground.length || x >= ground[0]?.length) return false;
    const t = ground[y][x];
    if (this.meta) {
      const terrain = this.meta.getTerrainForTile(t);
      return terrain === 'grass' || terrain === 'sidewalk' || terrain === 'asphalt' ||
             terrain === 'dirt' || terrain === 'sand';
    }
    return t === ZONE.GRASS || t === ZONE.DIRT || t === ZONE.ROAD ||
           t === ZONE.SIDEWALK || t === ZONE.ASPHALT || t === ZONE.PARKING ||
           t === ZONE.DRIVEWAY || t === ZONE.SAND || t === ZONE.PATH;
  }

  _isFloorTile(tileId) {
    if (this.meta) return this.meta.getRole(tileId) === 'floor';
    return tileId === ZONE.FLOOR || (tileId >= ZONE.BEDROOM && tileId <= ZONE.RESTROOM);
  }

  _isOutdoorTile(tileId) {
    if (this.meta) {
      const terrain = this.meta.getTerrainForTile(tileId);
      return terrain === 'grass' || terrain === 'sidewalk' || terrain === 'asphalt' ||
             terrain === 'dirt' || terrain === 'sand';
    }
    return tileId === ZONE.GRASS || tileId === ZONE.DIRT || tileId === ZONE.ROAD ||
           tileId === ZONE.SIDEWALK || tileId === ZONE.ASPHALT || tileId === ZONE.PARKING ||
           tileId === ZONE.DRIVEWAY || tileId === ZONE.SAND || tileId === ZONE.PATH;
  }
}
